import type {
  EndpointTestRunGroupEnvironment,
  EndpointTestRunGroupSummary,
  EndpointTestRunGroupVersion,
} from '@/types/endpointTests';
import type { RunCost } from '@/types';
import { formatRunCost } from '@/utils/formatUtils';
import { BARE_URL_PATH, formatDuration, testCount } from '@/utils/testRunResults';

interface TestRunSummaryStripProps {
  summary: EndpointTestRunGroupSummary;
  environment?: EndpointTestRunGroupEnvironment | null;
  version?: EndpointTestRunGroupVersion | null;
  /** The test's priced usage (task 1253); the item is omitted when absent or null. */
  cost?: RunCost | null;
  /** Actions (Re-run, Re-run failed, Compare, Export, Delete) on the strip's second line. */
  children?: React.ReactNode;
}

function Count({ testId, dot, value, label, muted }: {
  testId: string;
  dot: string;
  value: number;
  label: string;
  muted?: boolean;
}) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-sm ${
        muted ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'
      }`}
    >
      <span className={`w-2 h-2 rounded-full ${dot}`} />
      <span className="font-semibold">{value}</span> {label}
    </span>
  );
}

const chipClass =
  'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';

/**
 * The test run summary strip (task 1177; reused by the comparison report,
 * task 1178): pass / fail / error counts, the completed-without-verdict count
 * (neutral, never shown as passed), tests · duration, the environment and
 * version chips (absent for working state) and the reviewed count. Identical on
 * a list row and on an open test run.
 */
export function TestRunSummaryStrip({ summary, environment, version, cost, children }: TestRunSummaryStripProps) {
  const tests = testCount(summary);
  // A version with no stage ran on the bare URL (task 1237): the chip says "/".
  const path = environment?.path ?? (version ? BARE_URL_PATH : null);
  return (
    <div data-testid="test-run-strip" className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <Count testId="strip-passed" dot="bg-green-500" value={summary.passed} label="passed" />
        <Count testId="strip-failed" dot="bg-red-500" value={summary.failed} label="failed" />
        <Count testId="strip-errored" dot="bg-amber-500" value={summary.errored} label="errored" />
        {(summary.uncertain ?? 0) > 0 && (
          <Count testId="strip-uncertain" dot="bg-amber-300" value={summary.uncertain ?? 0} label="uncertain" />
        )}
        {summary.completed > 0 && (
          <Count testId="strip-completed" dot="bg-gray-400" value={summary.completed} label="completed" muted />
        )}
        {/* Refused before a collaboration started (task 1229); listed in the detail. */}
        {(summary.notRun ?? 0) > 0 && (
          <Count testId="strip-not-run" dot="bg-gray-300 dark:bg-gray-500" value={summary.notRun ?? 0} label="not run" muted />
        )}
        {(summary.pending ?? 0) > 0 && (
          <Count testId="strip-pending" dot="bg-gray-400 animate-pulse" value={summary.pending ?? 0} label="running" muted />
        )}
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {tests} {tests === 1 ? 'scenario' : 'scenarios'} · {formatDuration(summary.duration)}
        </span>
        {path && (
          <span className={chipClass} data-testid="strip-environment">{path}</span>
        )}
        {version && (
          <span className={chipClass} data-testid="strip-version">v{version.major}.{version.minor}</span>
        )}
        <span className="text-xs text-gray-500 dark:text-gray-400">{summary.reviewed} reviewed</span>
        {cost && (
          <span data-testid="strip-cost" className="text-xs text-gray-500 dark:text-gray-400">
            {formatRunCost(cost)}
          </span>
        )}
      </div>
      {children && <div className="flex flex-wrap items-center gap-x-3 gap-y-1">{children}</div>}
    </div>
  );
}

export default TestRunSummaryStrip;
